import {
  sortByDate,
  sortCustomId,
  sortNumber,
  sortString,
  TableSortData,
} from "@pimo/pimo-components";
import dayjs from "dayjs";
import {
  APP_CRITICALITIES,
  APP_RECORD_TYPES,
  AppCriticality,
  BasicItem,
  Project,
  ReportingItem,
  StatusDefinition,
} from "vmd-types";

type SortDirection = "ASC" | "DESC";

export type ChannelStats = {
  channelId: string;
  channelLabel: string;
  totalSystems: number;
  businessApps: number;
  itServices: number;
  criticalSystems: number;
};

type ChannelStatsSortableFields = {
  channel: string;
  channelLabel: string;
  totalSystems: number;
  businessApps: number;
  itServices: number;
  criticalSystems: number;
};

export type ChannelStatsSortData = TableSortData<ChannelStatsSortableFields>;

export type ReportingItemSortableFields = BasicItem &
  ReportingItem & {
    recordSource?: string;
    realizesDomainsAggregated?: string;
    dcRelevance?: string;
    dcRelevantOe?: string;
    llmSuggested?: string;
    updateStatus?: string;
    _degreeOfCompletionOrder?: number;
  };

type CustomFieldValueEntry = {
  value?: unknown;
  customFieldDefinition?: {
    label?: string;
  };
};

const toCustomColumnFieldName = (label?: string): string => {
  const normalizedLabel = (label ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return `customField-${normalizedLabel || "unnamed"}`;
};

const isValidDateValue = (value: unknown): value is string => {
  if (typeof value !== "string" || !value.trim()) {
    return false;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed);
};

const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const compareUnknownValues = (
  a: unknown,
  b: unknown,
  direction: SortDirection
): number => {
  const aNumber = toNumberOrNull(a);
  const bNumber = toNumberOrNull(b);

  if (aNumber != null && bNumber != null) {
    return sortNumber(aNumber, bNumber, direction);
  }

  if (isValidDateValue(a) && isValidDateValue(b)) {
    return sortByDate(a, b, direction);
  }

  return sortString(String(a ?? ""), String(b ?? ""), direction);
};

const compareCriticality = (
  a: string | undefined,
  b: string | undefined,
  direction: SortDirection
): number => {
  const criticalityOrder = Object.values(APP_CRITICALITIES);
  const aRank = a ? criticalityOrder.indexOf(a as AppCriticality) : -1;
  const bRank = b ? criticalityOrder.indexOf(b as AppCriticality) : -1;

  if (aRank < 0 && bRank < 0) {
    return sortString(a ?? "", b ?? "", direction);
  }

  return sortNumber(aRank, bRank, direction);
};

// Looks up the configured rank for a status-like value (e.g. degree of
// completion) by matching its stored name against the definitions configured
// for that field. Unranked values use the largest numeric rank and therefore
// sort last in ascending order and first in descending order.
export const resolveStatusOrder = (
  definitions: StatusDefinition[] | undefined,
  name: string | undefined
): number => {
  if (!name) {
    return Number.MAX_SAFE_INTEGER;
  }

  const match = definitions?.find((definition) => definition.name === name);
  return match ? match.order : Number.MAX_SAFE_INTEGER;
};

const UPDATE_STATUS_ORDER = ["Completed", "Updated", "Pending", "Overdue"];

const getUpdateStatusRank = (project: Project): number => {
  const completionDate = project?._calculated?.completionDate;
  const isCompleted =
    !!completionDate &&
    dayjs(completionDate).isValid() &&
    dayjs(completionDate).isBefore(dayjs());

  if (isCompleted) {
    return UPDATE_STATUS_ORDER.indexOf("Completed");
  }

  const rank = UPDATE_STATUS_ORDER.indexOf(
    project?._calculated?.updateStatus?.status ?? ""
  );

  return rank < 0 ? UPDATE_STATUS_ORDER.length : rank;
};

const getCustomFieldValueBySortKey = (
  customFields: CustomFieldValueEntry[] | undefined,
  sortKey: string
): unknown => {
  if (!sortKey.startsWith("customField-") || !customFields?.length) {
    return undefined;
  }

  const byFieldName = new Map<string, unknown>();
  const byLabelKey = new Map<string, unknown>();

  for (const customFieldValue of customFields) {
    const fieldName = toCustomColumnFieldName(
      customFieldValue.customFieldDefinition?.label
    );
    const labelKey = String(customFieldValue.customFieldDefinition?.label ?? "")
      .trim()
      .toLowerCase();

    const nextValue = customFieldValue.value;
    const currentValue = byFieldName.get(fieldName);
    const currentLabelValue = byLabelKey.get(labelKey);

    const hasMeaningfulValue =
      nextValue != null && String(nextValue).trim() !== "";
    const currentHasMeaningfulValue =
      currentValue != null && String(currentValue).trim() !== "";
    const labelHasMeaningfulValue =
      currentLabelValue != null && String(currentLabelValue).trim() !== "";

    if (!currentHasMeaningfulValue || hasMeaningfulValue) {
      byFieldName.set(fieldName, nextValue);
    }

    if (labelKey && (!labelHasMeaningfulValue || hasMeaningfulValue)) {
      byLabelKey.set(labelKey, nextValue);
    }
  }

  const sortLabelKey = sortKey.replace(/^customField-/, "").toLowerCase();

  return byFieldName.get(sortKey) ?? byLabelKey.get(sortLabelKey);
};

export function sortProjects(
  items: Project[],
  sort: TableSortData<
    Project & {
      progress: number;
      severity: string;
      status: string;
      degreeOfCompletion: string;
      endDate: string;
      updateStatus: string;
      numSystems: number;
      numApps: number;
      numITServices: number;
      numDistributionChannels: number;
    }
  >
): Project[] {
  const sortKey = String(sort.key);

  switch (sort.key) {
    case "customId":
    case "id":
      items.sort((a, b) =>
        sortCustomId(a?.customId ?? "", b?.customId ?? "", sort.direction)
      );
      break;
    case "name":
      items.sort((a, b) =>
        sortString(a?.name ?? "", b?.name ?? "", sort.direction)
      );
      break;
    case "owner":
      items.sort((a, b) =>
        sortString(a?.owner ?? "", b?.owner ?? "", sort.direction)
      );
      break;
    case "numSystems":
      items.sort((a, b) =>
        sortNumber(
          (a.reportingItems ?? []).length,
          (b.reportingItems ?? []).length,
          sort.direction
        )
      );
      break;
    case "numApps":
      items.sort((a, b) =>
        sortNumber(
          (a.reportingItems ?? []).filter(
            (item) =>
              !item.isChannelHeader &&
              item.basicItem?.recordType ===
                APP_RECORD_TYPES.BUSINESS_APPLICATION
          ).length,
          (b.reportingItems ?? []).filter(
            (item) =>
              !item.isChannelHeader &&
              item.basicItem?.recordType ===
                APP_RECORD_TYPES.BUSINESS_APPLICATION
          ).length,
          sort.direction
        )
      );
      break;
    case "numITServices":
      items.sort((a, b) =>
        sortNumber(
          (a.reportingItems ?? []).filter(
            (item) =>
              !item.isChannelHeader &&
              item.basicItem?.recordType === APP_RECORD_TYPES.IT_SERVICE
          ).length,
          (b.reportingItems ?? []).filter(
            (item) =>
              !item.isChannelHeader &&
              item.basicItem?.recordType === APP_RECORD_TYPES.IT_SERVICE
          ).length,
          sort.direction
        )
      );
      break;
    case "numDistributionChannels":
      items.sort((a, b) =>
        sortNumber(
          new Set(
            (a.reportingItems ?? [])
              .filter((item) => !item.isChannelHeader)
              .map((item) => item.distributionChannel)
              .filter((channel) => channel != null)
          ).size,
          new Set(
            (b.reportingItems ?? [])
              .filter((item) => !item.isChannelHeader)
              .map((item) => item.distributionChannel)
              .filter((channel) => channel != null)
          ).size,
          sort.direction
        )
      );
      break;
    case "severity":
      break;
    case "status":
      break;
    case "degreeOfCompletion":
      items.sort((a, b) =>
        sortNumber(
          a._calculated?.overallDegreeOfCompletion?.order ??
            Number.MAX_SAFE_INTEGER,
          b._calculated?.overallDegreeOfCompletion?.order ??
            Number.MAX_SAFE_INTEGER,
          sort.direction
        )
      );
      break;
    case "progress":
      items.sort((a, b) =>
        sortNumber(
          a._calculated?.averageProgress ?? -1,
          b._calculated?.averageProgress ?? -1,
          sort.direction
        )
      );
      break;
    case "endDate":
      items.sort((a, b) => sortByDate(a?.endDate, b?.endDate, sort.direction));
      break;
    case "updateStatus":
      items.sort((a, b) =>
        sortNumber(
          getUpdateStatusRank(a),
          getUpdateStatusRank(b),
          sort.direction
        )
      );
      break;
  }

  if (sortKey.startsWith("customField-")) {
    items.sort((a, b) =>
      compareUnknownValues(
        getCustomFieldValueBySortKey(
          (a as Project & { customFields?: CustomFieldValueEntry[] })
            .customFields,
          sortKey
        ),
        getCustomFieldValueBySortKey(
          (b as Project & { customFields?: CustomFieldValueEntry[] })
            .customFields,
          sortKey
        ),
        sort.direction
      )
    );
  }

  return items;
}

export function sortReportingItems(
  items: ReportingItem[],
  // Derived display values are precomputed by callers because this helper
  // does not have access to their config and form-answer lookups.
  sort: TableSortData<ReportingItemSortableFields>
): ReportingItem[] {
  const sortKey = String(sort.key);

  switch (sort.key) {
    case "customId":
      items.sort((a, b) =>
        sortCustomId(a?.customId ?? "", b?.customId ?? "", sort.direction)
      );
      break;
    case "name":
      items.sort((a, b) =>
        sortString(
          a?.basicItem?.name ?? "",
          b?.basicItem?.name ?? "",
          sort.direction
        )
      );
      break;
    case "recordType":
      items.sort((a, b) =>
        sortString(
          a?.basicItem?.recordType ?? "",
          b?.basicItem?.recordType ?? "",
          sort.direction
        )
      );
      break;
    case "owner":
      items.sort((a, b) =>
        sortString(
          a?.basicItem?.owner ?? "",
          b?.basicItem?.owner ?? "",
          sort.direction
        )
      );
      break;
    case "distributionChannel":
      items.sort((a, b) =>
        sortString(
          (a?.distributionChannel ?? []).join(","),
          (b?.distributionChannel ?? []).join(","),
          sort.direction
        )
      );
      break;
    case "lifeCycle":
      items.sort((a, b) =>
        sortString(
          a?.basicItem?.lifeCycle ?? "",
          b?.basicItem?.lifeCycle ?? "",
          sort.direction
        )
      );
      break;
    case "criticality":
      items.sort((a, b) =>
        compareCriticality(
          a?.basicItem?.criticality ?? "",
          b?.basicItem?.criticality ?? "",
          sort.direction
        )
      );
      break;
    case "processSteps":
      items.sort((a, b) =>
        sortString(
          (a?.basicItem?.processSteps ?? []).filter(Boolean).join(", "),
          (b?.basicItem?.processSteps ?? []).filter(Boolean).join(", "),
          sort.direction
        )
      );
      break;
    case "applicationCapabilities":
      items.sort((a, b) =>
        sortString(
          (a?.basicItem?.applicationCapabilities ?? [])
            .filter(Boolean)
            .join(", "),
          (b?.basicItem?.applicationCapabilities ?? [])
            .filter(Boolean)
            .join(", "),
          sort.direction
        )
      );
      break;
    case "technicalExposure":
      items.sort((a, b) =>
        sortString(
          (a?.basicItem?.technicalExposure ?? []).filter(Boolean).join(", "),
          (b?.basicItem?.technicalExposure ?? []).filter(Boolean).join(", "),
          sort.direction
        )
      );
      break;
    // recordSource, realizesDomainsAggregated, dcRelevance, dcRelevantOe and
    // llmSuggested
    // are derived values (form answers, review status) that this function
    // has no access to compute - callers precompute them onto each row
    // before sorting (see getRecordSource / getFormAnswerValues /
    // getDcRelevanceDisplay / getDcRelevantOeDisplay at the call sites).
    case "recordSource":
      items.sort((a, b) =>
        sortString(
          (a as unknown as { recordSource?: string }).recordSource ?? "",
          (b as unknown as { recordSource?: string }).recordSource ?? "",
          sort.direction
        )
      );
      break;
    case "realizesDomainsAggregated":
      items.sort((a, b) =>
        sortString(
          (a as unknown as { realizesDomainsAggregated?: string })
            .realizesDomainsAggregated ?? "",
          (b as unknown as { realizesDomainsAggregated?: string })
            .realizesDomainsAggregated ?? "",
          sort.direction
        )
      );
      break;
    case "dcRelevance":
      items.sort((a, b) =>
        sortString(
          (a as unknown as { dcRelevance?: string }).dcRelevance ?? "",
          (b as unknown as { dcRelevance?: string }).dcRelevance ?? "",
          sort.direction
        )
      );
      break;
    case "dcRelevantOe":
      items.sort((a, b) =>
        sortString(
          (a as unknown as { dcRelevantOe?: string }).dcRelevantOe ?? "",
          (b as unknown as { dcRelevantOe?: string }).dcRelevantOe ?? "",
          sort.direction
        )
      );
      break;
    case "llmSuggested":
      items.sort((a, b) =>
        sortString(
          (a as unknown as { llmSuggested?: string }).llmSuggested ?? "",
          (b as unknown as { llmSuggested?: string }).llmSuggested ?? "",
          sort.direction
        )
      );
      break;
    case "severity":
      break;
    case "status":
      break;
    case "degreeOfCompletion":
      // Not alphabetical on purpose: degree of completion is an ordered
      // status (e.g. Not started -> In Progress -> ... -> Submitted), and
      // that order comes from settings.configuration.degreeOfCompletionDefinition,
      // which this function cannot see. Callers precompute the numeric rank
      // via resolveStatusOrder into _degreeOfCompletionOrder.
      items.sort((a, b) =>
        sortNumber(
          (a as unknown as { _degreeOfCompletionOrder?: number })
            ._degreeOfCompletionOrder ?? Number.MAX_SAFE_INTEGER,
          (b as unknown as { _degreeOfCompletionOrder?: number })
            ._degreeOfCompletionOrder ?? Number.MAX_SAFE_INTEGER,
          sort.direction
        )
      );
      break;
    case "progress":
      items.sort((a, b) =>
        sortNumber(a.progress ?? -1, b.progress ?? -1, sort.direction)
      );
      break;
    case "budget":
      items.sort((a, b) =>
        sortNumber(
          a?.basicItem?.budget ?? -1,
          b?.basicItem?.budget ?? -1,
          sort.direction
        )
      );
      break;
    case "endDate":
      items.sort((a, b) =>
        sortByDate(a?.basicItem?.endDate, b?.basicItem?.endDate, sort.direction)
      );
      break;
    // TODO:: enable once update status has been implemented
    // case "updateStatus":
    //   break;
  }

  if (sortKey.startsWith("customField-")) {
    items.sort((a, b) =>
      compareUnknownValues(
        getCustomFieldValueBySortKey(
          (a as ReportingItem & { customFields?: CustomFieldValueEntry[] })
            .customFields,
          sortKey
        ),
        getCustomFieldValueBySortKey(
          (b as ReportingItem & { customFields?: CustomFieldValueEntry[] })
            .customFields,
          sortKey
        ),
        sort.direction
      )
    );
  }

  return items;
}

export function sortChannelStats(
  items: ChannelStats[],
  sort: ChannelStatsSortData
): ChannelStats[] {
  switch (sort.key) {
    case "channel":
    case "channelLabel":
      items.sort((a, b) =>
        sortString(a.channelLabel ?? "", b.channelLabel ?? "", sort.direction)
      );
      break;
    case "totalSystems":
      items.sort((a, b) =>
        sortNumber(a.totalSystems ?? 0, b.totalSystems ?? 0, sort.direction)
      );
      break;
    case "businessApps":
      items.sort((a, b) =>
        sortNumber(a.businessApps ?? 0, b.businessApps ?? 0, sort.direction)
      );
      break;
    case "itServices":
      items.sort((a, b) =>
        sortNumber(a.itServices ?? 0, b.itServices ?? 0, sort.direction)
      );
      break;
    case "criticalSystems":
      items.sort((a, b) =>
        sortNumber(
          a.criticalSystems ?? 0,
          b.criticalSystems ?? 0,
          sort.direction
        )
      );
      break;
  }

  return items;
}
